import React, { useState, useEffect } from 'react';
import { Bell, Send, RefreshCw, Users, Globe, MapPin, Hash } from 'lucide-react';
import {
  fetchCountriesForDropdown, fetchStatesByCountry,
  fetchUserPostcodes, fetchBulkNotificationCount, sendBulkNotification,
} from '../services/api.ts';
import './BulkNotifications.css';

const BulkNotifications: React.FC = () => {
  // Filter state
  const [countries, setCountries] = useState<any[]>([]);
  const [states, setStates] = useState<any[]>([]);
  const [postcodes, setPostcodes] = useState<string[]>([]);
  const [countryId, setCountryId] = useState<number | ''>('');
  const [stateId, setStateId] = useState<number | ''>('');
  const [postcode, setPostcode] = useState('');

  // Message state
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');

  // Status
  const [userCount, setUserCount] = useState<number | null>(null);
  const [tokenCount, setTokenCount] = useState<number | null>(null);
  const [counting, setCounting] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ status: string; message: string; success_count?: number; failure_count?: number } | null>(null);

  // Load countries on mount
  useEffect(() => {
    fetchCountriesForDropdown().then(setCountries).catch(console.error);
  }, []);

  // Load states when country changes
  useEffect(() => {
    setStates([]);
    setStateId('');
    setPostcodes([]);
    setPostcode('');
    if (countryId) {
      fetchStatesByCountry(Number(countryId)).then(setStates).catch(console.error);
    }
  }, [countryId]);

  // Load postcodes when state changes
  useEffect(() => {
    setPostcodes([]);
    setPostcode('');
    if (stateId) {
      fetchUserPostcodes(Number(stateId)).then(setPostcodes).catch(console.error);
    }
  }, [stateId]);

  // Fetch matching user count when filters change
  useEffect(() => {
    setCounting(true);
    setUserCount(null);
    setTokenCount(null);
    setResult(null);
    const params: any = {};
    if (countryId) params.country_id = countryId;
    if (stateId) params.state_id = stateId;
    if (postcode) params.postcode = postcode;
    fetchBulkNotificationCount(params)
      .then(data => { setUserCount(data.user_count); setTokenCount(data.token_count); })
      .catch(() => { setUserCount(0); setTokenCount(0); })
      .finally(() => setCounting(false));
  }, [countryId, stateId, postcode]);

  const scopeLabel = () => {
    if (postcode) return `postcode ${postcode}`;
    if (stateId) {
      const st = states.find(s => s.id === Number(stateId));
      return st ? st.name : 'selected state';
    }
    if (countryId) {
      const c = countries.find(co => co.id === Number(countryId));
      return c ? c.name : 'selected country';
    }
    return 'all users';
  };

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const params: any = { title: title.trim(), body: body.trim() };
      if (countryId) params.country_id = countryId;
      if (stateId) params.state_id = stateId;
      if (postcode) params.postcode = postcode;
      const res = await sendBulkNotification(params);
      setResult(res);
      if (res.status === 'success') {
        setTitle('');
        setBody('');
      }
    } catch {
      setResult({ status: 'error', message: 'Failed to send notification. Please try again.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bn-page">
      <div className="bn-header">
        <div className="bn-header-left">
          <Bell size={24} />
          <h1>Bulk Notifications</h1>
        </div>
        <p className="bn-subtitle">Send push notifications to groups of users filtered by location</p>
      </div>

      <div className="bn-content">
        {/* Audience */}
        <div className="bn-card">
          <h2 className="bn-card-title"><Users size={18} /> Audience</h2>

          <div className="bn-filters">
            <div className="bn-field">
              <label><Globe size={14} /> Country</label>
              <select value={countryId} onChange={e => setCountryId(e.target.value ? Number(e.target.value) : '')}>
                <option value="">All countries</option>
                {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div className="bn-field">
              <label><MapPin size={14} /> State</label>
              <select value={stateId} onChange={e => setStateId(e.target.value ? Number(e.target.value) : '')} disabled={!countryId}>
                <option value="">All states</option>
                {states.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>

            <div className="bn-field">
              <label><Hash size={14} /> Postcode</label>
              <select value={postcode} onChange={e => setPostcode(e.target.value)} disabled={!stateId}>
                <option value="">All postcodes</option>
                {postcodes.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          <div className="bn-audience-info">
            {counting ? (
              <span className="bn-counting"><RefreshCw size={14} className="spin" /> Counting users…</span>
            ) : userCount !== null ? (
              <span className="bn-count">
                <strong>{userCount.toLocaleString()}</strong> user{userCount !== 1 ? 's' : ''} matched
                {tokenCount !== null && <> · <strong>{tokenCount.toLocaleString()}</strong> device{tokenCount !== 1 ? 's' : ''} with push enabled</>}
              </span>
            ) : null}
          </div>
        </div>

        {/* Message */}
        <div className="bn-card">
          <h2 className="bn-card-title"><Send size={18} /> Message</h2>

          <div className="bn-field">
            <label>Title</label>
            <input
              type="text"
              placeholder="Notification title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className="bn-field">
            <label>Body</label>
            <textarea
              placeholder="Notification message"
              value={body}
              onChange={e => setBody(e.target.value)}
              rows={4}
              maxLength={500}
            />
          </div>

          {result && (
            <div className={`bn-result ${result.status}`}>
              <p>{result.message}</p>
              {result.success_count !== undefined && (
                <span className="bn-result-detail">
                  {result.success_count} sent · {result.failure_count} failed
                </span>
              )}
            </div>
          )}

          <button
            className="bn-send-btn"
            onClick={handleSend}
            disabled={sending || !title.trim() || !body.trim() || tokenCount === 0}
          >
            {sending ? (
              <><RefreshCw size={16} className="spin" /> Sending to {scopeLabel()}…</>
            ) : (
              <><Send size={16} /> Send to {scopeLabel()}</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkNotifications;
